import React, { useState } from 'react';
import { Layout } from '../components/Layout';
import { SEO } from '../components/SEO';
import { Card, CardContent } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { CheckCircle2, MapPin, Heart, Users, Briefcase, Megaphone, TrendingUp, ChevronDown, ChevronUp, Award, Clock, Shield, FileText, ArrowRight, CheckSquare } from 'lucide-react';
import { sendViaWeb3Forms } from '../services/web3formsService';
import { saveSubmission, type BenevoleSubmission } from '../services/databaseService';
// --- DATA ---
const POLES = [{
  id: 'terrain',
  title: 'Terrain & événements',
  desc: 'Mobilisation, accueil et logistique sur le terrain',
  color: 'bg-blue-50 border-blue-200 text-blue-700',
  icon: Users,
  roles: ['Acteur terrain & events', 'Hôte / Hôtesse', 'Coursier(ère)']
}, {
  id: 'admin',
  title: 'Administration & appui',
  desc: 'Gestion administrative, financière et juridique',
  color: 'bg-green-50 border-green-200 text-green-700',
  icon: FileText,
  roles: ['Assistant(e) administratif(ve)', 'Comptable', 'Juriste']
}, {
  id: 'comms',
  title: 'Communication & image',
  desc: "Création de contenu et visibilité de l'association",
  color: 'bg-purple-50 border-purple-200 text-purple-700',
  icon: Megaphone,
  roles: ['Créateur(trice) contenu', 'Photographe', 'Graphic designer']
}, {
  id: 'dev',
  title: 'Développement & insertion',
  desc: 'Accompagnement des bénéficiaires et développement',
  color: 'bg-amber-50 border-amber-200 text-amber-700',
  icon: TrendingUp,
  roles: ['Accompagnateur(trice) insertion', 'Technico-commercial(e)', 'Commercial(e) / Qualiticien(ne)']
}];
const FICHES = [{
  role: 'Accompagnateur(trice) insertion',
  pole: 'Développement & insertion',
  mission: "Guider les bénéficiaires à chaque étape de leur parcours, avant et après les ateliers de renforcement de capacités, jusqu'à leur pleine intégration professionnelle.",
  responsabilites: ['Pré-insertion : accueil, évaluation des besoins, orientation', 'Préparation des bénéficiaires aux ateliers', "Post-insertion : accompagnement individualisé après les ateliers jusqu'à l'emploi", 'Aide à la rédaction de CV et lettres de motivation', "Mise en relation avec le réseau d'employeurs", 'Suivi régulier des bénéficiaires placés', 'Contribution à la lutte contre les abus'],
  profil: "Empathie profonde, patience et écoute active. Connaissance du marché de l'emploi local. Sensibilité aux questions de genre. Expérience en accompagnement social appréciée.",
  dispo: 'Flexible, adaptée à votre disponibilité'
}, {
  role: 'Assistant(e) administratif(ve)',
  pole: 'Administration & appui',
  mission: "Pilier organisationnel d'ADCC, soutien au Bureau exécutif dans la gestion quotidienne.",
  responsabilites: ['Appui administratif au Bureau exécutif', 'Gestion et archivage des documents statutaires', 'Suivi des adhésions des membres', 'Rédaction de comptes-rendus', 'Préparation logistique des AG', 'Coordination des communications internes'],
  profil: "Sens de l'organisation, rigueur et discrétion. Maîtrise des outils bureautiques. Adhésion aux valeurs ADCC. Bon relationnel.",
  dispo: 'Flexible'
}, {
  role: 'Commercial(e) / Qualiticien(ne)',
  pole: 'Développement & insertion',
  mission: 'Vision commerciale et exigence qualité pour faire rayonner ADCC et assurer la crédibilité auprès des institutions.',
  responsabilites: ['Mise en place et suivi des indicateurs qualité', 'Amélioration continue des processus', 'Contribution aux bilans qualitatifs et quantitatifs', 'Appui aux demandes de subventions', 'Développement des relations avec institutions et ONG', 'Veille sur les standards'],
  profil: 'Formation en management de la qualité ou commerce. Rigueur analytique. Capacité à rédiger des rapports. Intérêt pour le développement associatif.',
  dispo: 'Flexible'
}, {
  role: 'Comptable',
  pole: 'Administration & appui',
  mission: "Garantir la transparence et la pérennité financière d'ADCC.",
  responsabilites: ['Appui au Trésorier dans la comptabilité générale', 'Suivi des cotisations et dépenses', 'Enregistrement des ressources', 'Préparation des bilans financiers annuels', 'Gestion des factures et notes de frais', 'Conformité aux normes comptables béninoises'],
  profil: 'Formation ou expérience en comptabilité/finances. Rigueur et fiabilité. Connaissance des normes comptables béninoises appréciée. Intégrité et confidentialité.',
  dispo: 'Flexible'
}, {
  role: 'Coursier(ère)',
  pole: 'Terrain & événements',
  mission: 'Lien concret entre ADCC et son environnement, faciliter la logistique.',
  responsabilites: ['Livraison et collecte de documents officiels', 'Déplacements vers les administrations et partenaires', 'Appui logistique lors des ateliers et événements', "Transmission d'informations aux bénéficiaires", 'Petites missions pratiques'],
  profil: 'Ponctualité, fiabilité et sens du service. Bonne connaissance de Cotonou. Disponibilité ponctuelle. Adhésion aux valeurs ADCC.',
  dispo: 'Ponctuelle'
},
// Placeholders for the rest
{
  role: 'Acteur terrain & events',
  pole: 'Terrain & événements',
  mission: "Appui opérationnel lors des déploiements et événements de l'association.",
  responsabilites: ['Installation logistique', 'Accueil du public', 'Gestion des flux lors des événements', 'Appui aux formateurs'],
  profil: 'Dynamique, proactif(ve) et aimant le contact humain.',
  dispo: 'Ponctuelle (lors des événements)'
}, {
  role: 'Hôte / Hôtesse',
  pole: 'Terrain & événements',
  mission: 'Accueil et orientation du public lors de nos événements et ateliers.',
  responsabilites: ['Accueil physique', 'Émargement des participants', 'Orientation et information', 'Remise de documents'],
  profil: 'Souriant(e), excellente présentation, sens du service et courtoisie.',
  dispo: 'Ponctuelle'
}, {
  role: 'Juriste',
  pole: 'Administration & appui',
  mission: 'Votre expertise juridique protège ADCC et ses bénéficiaires. Dans une association engagée pour la dignité au travail et la lutte contre les abus, votre rôle est stratégique pour sécuriser toutes nos actions.',
  responsabilites: ["Conseil juridique au Bureau exécutif sur les activités de l'association", 'Rédaction et vérification de contrats, conventions et partenariats', 'Appui aux actions de sensibilisation aux droits des travailleurs/ses', 'Veille sur le droit associatif, le droit du travail et la protection sociale au Bénin', "Assistance lors des déclarations et obligations légales de l'association", "Soutien aux bénéficiaires confrontés à des situations d'abus ou de violence au travail"],
  profil: 'Formation en droit (licence minimum). Connaissance du droit béninois et du droit associatif appréciée. Capacité à vulgariser les concepts juridiques. Sensibilité aux questions de genre et de droits des travailleuses.',
  dispo: 'Flexible'
}, {
  role: 'Créateur(trice) contenu',
  pole: 'Communication & image',
  mission: "Vous êtes la voix engagée d'ADCC ! Par vos mots et vos idées, vous sensibilisez le grand public à l'autonomisation des femmes et des jeunes, valorisez les parcours des bénéficiaires et renforcez la visibilité de l'association.",
  responsabilites: ["Rédaction de posts, articles et newsletters valorisant les missions d'ADCC", 'Création de contenu engagé sur les réseaux sociaux (Facebook, Instagram, LinkedIn…)', 'Mise en valeur des témoignages et parcours de bénéficiaires (dans le respect de leur intimité)', 'Sensibilisation aux droits des travailleur·ses et à la lutte contre les abus', "Mise à jour et animation du site internet de l'association", 'Suivi des statistiques de publication et adaptation de la stratégie éditoriale'],
  profil: "Excellentes qualités rédactionnelles en français. Sensibilité aux questions sociales, de genre et d'insertion professionnelle. Créativité, sens de la narration et de la pédagogie. Connaissance des réseaux sociaux et des outils de publication en ligne.",
  dispo: 'Flexible'
}, {
  role: 'Photographe',
  pole: 'Communication & image',
  mission: "À travers votre objectif, vous capturez l'essence d'ADCC : les visages déterminés, les compétences qui s'affirment, les parcours qui transforment. Vos images sont autant de témoignages de l'impact de l'association.",
  responsabilites: ["Couverture photo des ateliers, formations et événements d'ADCC", 'Portraits valorisants des bénéficiaires, bénévoles et partenaires (avec consentement)', 'Retouche et sélection des photographies pour les différents supports', "Alimentation et organisation de la photothèque de l'association", "Fourniture d'images pour le site internet, les réseaux sociaux et les rapports annuels"],
  profil: "Maîtrise de la photographie (appareil numérique reflex ou hybride). Connaissance des logiciels de retouche (Lightroom, Photoshop ou équivalent). Sens humain, respect et discrétion vis-à-vis des bénéficiaires. Disponibilité lors des événements ponctuels de l'association.",
  dispo: 'Ponctuelle'
}, {
  role: 'Graphic designer',
  pole: 'Communication & image',
  mission: "Vous donnez un visage à ADCC ! Votre créativité traduit visuellement les valeurs de dignité, compétence et création de l'association, rendant nos messages accessibles, attractifs et mémorables pour tous les publics.",
  responsabilites: ['Création de visuels pour les réseaux sociaux, le web et les campagnes de communication', 'Conception de flyers, affiches et supports pour les ateliers et événements', "Respect et développement de la charte graphique d'ADCC", 'Mise en page de documents officiels : rapports annuels, plaquettes, attestations', 'Infographies pédagogiques pour la sensibilisation aux droits des travailleur·ses'],
  profil: "Maîtrise des logiciels de design (Illustrator, InDesign, Canva ou équivalents). Sens esthétique, créativité et rigueur graphique. Capacité à adapter son style aux différents supports et publics cibles. Intérêt pour les causes sociales et l'Afrique de l'Ouest.",
  dispo: 'Flexible'
}, {
  role: 'Technico-commercial(e)',
  pole: 'Développement & insertion',
  mission: "Votre double expertise technique et commerciale est un levier stratégique pour ADCC. Vous bâtissez les ponts entre l'association et le monde économique, créant des opportunités concrètes pour les bénéficiaires et des ressources pour les programmes.",
  responsabilites: ["Prospection d'entreprises, hôtels, restaurants et familles susceptibles d'embaucher des bénéficiaires", "Présentation des formations et compétences d'ADCC aux employeurs potentiels", 'Négociation et suivi de conventions de partenariat et de placement', 'Développement de nouvelles sources de financement (mécénat, sponsoring, subventions)', 'Participation aux événements économiques et de networking à Cotonou et au Bénin', 'Contribution aux appels à projets et demandes de subventions'],
  profil: "Expérience en développement commercial, vente ou négociation. Excellent relationnel et aisance dans les milieux professionnels variés. Esprit d'initiative, autonomie et orientation résultats. Adhésion à l'économie sociale et solidaire et aux valeurs d'ADCC.",
  dispo: 'Régulière'
}];
export function BenevolesPage() {
  const [submitted, setSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedRole, setSelectedRole] = useState('');
  const [openFiche, setOpenFiche] = useState<string | null>(null);
  const [charterAccepted, setCharterAccepted] = useState(false);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    const formData = new FormData(e.target as HTMLFormElement);
    const data = Object.fromEntries(formData.entries()) as Record<string, string>;
    // Ajouter explicitement la charte acceptée
    data.charteAcceptee = charterAccepted ? 'Oui' : 'Non';
    // Supprimer le champ checkbox brut
    delete data.charter;
    // Sauvegarder dans la base de données locale
    saveSubmission<BenevoleSubmission>({
      type: 'benevole',
      nom: data.nom || '',
      prenom: '',
      email: data.email || '',
      telephone: data.telephone || '',
      quartier: data.quartier || '',
      disponibilite: data.disponibilite || '',
      motivation: `[Métier souhaité : ${data.metier}] ${data.motivation || ''}`
    });
    // Envoyer via Web3Forms (email à contact@adcc.africa)
    const result = await sendViaWeb3Forms({
      type: 'benevole',
      fields: data
    });
    console.log('📋 Bénévole submission result:', result);
    setIsLoading(false);
    setSubmitted(true);
    window.scrollTo(0, 0);
  };
  const scrollToSection = (id: string) => {
    document.getElementById(id)?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  const handleRoleSelect = (role: string) => {
    setSelectedRole(role);
    scrollToSection('volunteer-form');
  };
  const toggleFiche = (role: string) => {
    setOpenFiche(openFiche === role ? null : role);
  };
  // --- 9. CONFIRMATION ---
  if (submitted) {
    return <Layout data-id="element-779">
        <div className="min-h-[60vh] flex items-center justify-center bg-gray-50 px-4 py-12" data-id="element-780">
          <Card className="max-w-md w-full text-center p-10 shadow-lg border-t-4 border-t-[#1E88E5]" data-id="element-781">
            <div className="w-20 h-20 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-782">
              <CheckCircle2 className="w-10 h-10 text-green-600" data-id="element-783" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-784">
              Candidature envoyée !
            </h2>
            <p className="text-gray-600 mb-8 leading-relaxed" data-id="element-785">
              Merci pour votre engagement. L'équipe ADCC a bien reçu votre
              profil et l'étudiera avec attention. Nous vous recontacterons très
              prochainement.
            </p>
            <Button onClick={() => window.location.href = '/'} variant="outline" className="w-full" data-id="element-786">
              Retour à l'accueil
            </Button>
          </Card>
        </div>
      </Layout>;
  }
  return <Layout data-id="element-787">
      <SEO title="Devenir Bénévole - Rejoignez l'ADCC Bénin" description="Contribuez aux actions de l'ADCC au Bénin en mettant vos compétences au service de missions utiles. Découvrez nos métiers bénévoles." keywords="bénévolat Bénin, volontariat Cotonou, association humanitaire Bénin, engagement social, insertion professionnelle, bénévolat compétences" url="https://adcc.africa/benevoles" data-id="element-788" />

      {/* 1. HERO */}
      <section className="bg-[#2C3E50] text-white py-20 md:py-28" data-id="element-789">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-790">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight" data-id="element-791">
            Rejoignez le programme de bénévoles ADCC
          </h1>
          <p className="text-xl md:text-2xl text-gray-300 mb-10 max-w-3xl mx-auto leading-relaxed" data-id="element-792">
            Contribuez aux actions de l'ADCC au Bénin en mettant vos compétences
            au service de missions utiles.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4" data-id="element-793">
            <Button onClick={() => scrollToSection('volunteer-form')} size="lg" className="text-lg px-8 py-6" data-id="element-794">
              Je postule
            </Button>
            <Button onClick={() => scrollToSection('metiers')} variant="outline" size="lg" className="text-lg px-8 py-6 text-white border-white hover:bg-white/10 hover:text-white" data-id="element-795">
              Voir les métiers
            </Button>
          </div>
        </div>
      </section>

      {/* 2. MÉTIERS RECHERCHÉS */}
      <section id="metiers" className="py-20 bg-gray-50" data-id="element-796">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-797">
          <div className="text-center mb-16" data-id="element-798">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4" data-id="element-799">
              Métiers recherchés
            </h2>
            <p className="text-lg text-gray-600" data-id="element-800">
              Découvrez nos 4 pôles d'action et trouvez la mission qui vous
              correspond.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12" data-id="element-801">
            {POLES.map(pole => <Card key={pole.id} className="h-full flex flex-col border-t-4 hover:shadow-lg transition-shadow" style={{
            borderTopColor: pole.id === 'terrain' ? '#3B82F6' : pole.id === 'admin' ? '#10B981' : pole.id === 'comms' ? '#8B5CF6' : '#F59E0B'
          }} data-id="element-802">
                <CardContent className="p-6 flex-grow flex flex-col" data-id="element-803">
                  <div className={`w-12 h-12 rounded-lg flex items-center justify-center mb-6 ${pole.color}`} data-id="element-804">
                    <pole.icon className="w-6 h-6" data-id="element-805" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-806">
                    {pole.title}
                  </h3>
                  <p className="text-gray-600 text-sm mb-6 flex-grow" data-id="element-807">
                    {pole.desc}
                  </p>
                  <div className="space-y-2 mt-auto" data-id="element-808">
                    {pole.roles.map((role, idx) => <button key={idx} onClick={() => toggleFiche(role)} className={`w-full text-left text-sm px-3 py-1.5 rounded-md font-medium transition-colors ${openFiche === role ? 'bg-[#1E88E5] text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-809">
                        {role}
                      </button>)}
                  </div>
                </CardContent>
              </Card>)}
          </div>

          {/* Fiche détail inline */}
          {openFiche && (() => {
          const fiche = FICHES.find(f => f.role === openFiche);
          if (!fiche) return null;
          return <div className="mt-10 bg-white rounded-xl border border-gray-200 shadow-md p-6 md:p-8" data-id="element-810">
                  <div className="flex items-center justify-between mb-6" data-id="element-811">
                    <div data-id="element-812">
                      <h3 className="text-xl font-bold text-gray-900" data-id="element-813">
                        {fiche.role}
                      </h3>
                      <span className="text-sm text-gray-500" data-id="element-814">
                        {fiche.pole}
                      </span>
                    </div>
                    <button onClick={() => setOpenFiche(null)} className="text-gray-400 hover:text-gray-600 p-1" data-id="element-815">
                      <ChevronUp className="w-5 h-5" data-id="element-816" />
                    </button>
                  </div>

                  <div className="space-y-6" data-id="element-817">
                    <div data-id="element-818">
                      <h4 className="font-bold text-gray-900 mb-2 flex items-center gap-2" data-id="element-819">
                        <Briefcase className="w-4 h-4 text-[#1E88E5]" data-id="element-820" /> Mission
                      </h4>
                      <p className="text-gray-700 text-sm leading-relaxed" data-id="element-821">
                        {fiche.mission}
                      </p>
                    </div>

                    <div data-id="element-822">
                      <h4 className="font-bold text-gray-900 mb-2 flex items-center gap-2" data-id="element-823">
                        <CheckSquare className="w-4 h-4 text-[#1E88E5]" data-id="element-824" />{' '}
                        Responsabilités
                      </h4>
                      <ul className="list-disc pl-5 space-y-1 text-sm text-gray-700" data-id="element-825">
                        {fiche.responsabilites.map((resp, i) => <li key={i} data-id="element-826">{resp}</li>)}
                      </ul>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-827">
                      <div data-id="element-828">
                        <h4 className="font-bold text-gray-900 mb-2 flex items-center gap-2" data-id="element-829">
                          <Users className="w-4 h-4 text-[#1E88E5]" data-id="element-830" /> Profil
                          idéal
                        </h4>
                        <p className="text-gray-700 text-sm leading-relaxed" data-id="element-831">
                          {fiche.profil}
                        </p>
                      </div>
                      <div data-id="element-832">
                        <h4 className="font-bold text-gray-900 mb-2 flex items-center gap-2" data-id="element-833">
                          <Clock className="w-4 h-4 text-[#1E88E5]" data-id="element-834" />{' '}
                          Disponibilité
                        </h4>
                        <p className="text-gray-700 text-sm leading-relaxed" data-id="element-835">
                          {fiche.dispo}
                        </p>
                      </div>
                    </div>

                    <div className="pt-4" data-id="element-836">
                      <Button onClick={() => handleRoleSelect(fiche.role)} className="w-full sm:w-auto" data-id="element-837">
                        Choisir ce rôle
                      </Button>
                    </div>
                  </div>
                </div>;
        })()}
        </div>
      </section>

      {/* 4. PROFIL RECHERCHÉ */}
      <section className="py-20 bg-gray-50" data-id="element-838">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-839">
          <div className="text-center mb-12" data-id="element-840">
            <h2 className="text-3xl font-bold text-gray-900 mb-4" data-id="element-841">
              Profil recherché
            </h2>
          </div>
          <div className="max-w-3xl mx-auto space-y-4" data-id="element-842">
            {[{
            text: "Motivé(e) par l'impact social et l'insertion professionnelle",
            icon: Heart
          }, {
            text: 'Disponible ponctuellement ou régulièrement selon vos possibilités',
            icon: Clock
          }, {
            text: 'Fiable, organisé(e) et respectueux(se) des engagements',
            icon: Shield
          }, {
            text: 'Ouvert(e) au travail en équipe et à la diversité',
            icon: Users
          }, {
            text: 'Adhérant aux valeurs de dignité, compétence et création',
            icon: Award
          }].map((item, idx) => <div key={idx} className="flex items-center gap-4 bg-white p-4 rounded-lg shadow-sm border border-gray-100" data-id="element-843">
                <div className="p-2 bg-blue-50 rounded-full text-[#1E88E5] flex-shrink-0" data-id="element-844">
                  <item.icon className="w-5 h-5" data-id="element-845" />
                </div>
                <span className="text-gray-800 font-medium" data-id="element-846">{item.text}</span>
              </div>)}
          </div>
        </div>
      </section>

      {/* 5. CE QUE LE BÉNÉVOLAT VOUS APPORTE */}
      <section className="py-20 bg-white" data-id="element-847">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-848">
          <div className="text-center mb-16" data-id="element-849">
            <h2 className="text-3xl font-bold text-gray-900 mb-4" data-id="element-850">
              Ce que le bénévolat vous apporte
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-5xl mx-auto mb-12" data-id="element-851">
            {[{
            text: 'Expérience terrain concrète dans le secteur associatif',
            icon: MapPin
          }, {
            text: 'Développement de compétences professionnelles',
            icon: TrendingUp
          }, {
            text: 'Attestation officielle de bénévolat délivrée par ADCC',
            icon: FileText
          }, {
            text: 'Intégration dans un réseau professionnel engagé',
            icon: Users
          }, {
            text: 'Contribution directe à une cause utile au Bénin',
            icon: Heart
          }, {
            text: "Opportunité de carrière dans le secteur d'activité",
            icon: Briefcase
          }].map((item, idx) => <div key={idx} className="flex flex-col items-center text-center p-6 bg-gray-50 rounded-xl" data-id="element-852">
                <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-sm mb-4 text-[#1E88E5]" data-id="element-853">
                  <item.icon className="w-6 h-6" data-id="element-854" />
                </div>
                <p className="font-medium text-gray-900" data-id="element-855">{item.text}</p>
              </div>)}
          </div>

          <div className="max-w-3xl mx-auto bg-blue-50 rounded-xl p-6 text-center border border-blue-100" data-id="element-856">
            <p className="text-blue-800 font-medium" data-id="element-857">
              💡 Nous prévoyons également : Indemnités de transport, repas
              couverts lors des événements, et une flexibilité des horaires
              adaptée à vos contraintes.
            </p>
          </div>
        </div>
      </section>

      {/* 6. COMMENT ÇA SE PASSE ? */}
      <section className="py-20 bg-[#2C3E50] text-white" data-id="element-858">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-859">
          <div className="text-center mb-16" data-id="element-860">
            <h2 className="text-3xl font-bold mb-4" data-id="element-861">Comment ça se passe ?</h2>
          </div>

          <div className="flex flex-col md:flex-row justify-center items-start gap-8 md:gap-4 max-w-5xl mx-auto" data-id="element-862">
            {[{
            step: '1',
            title: 'Vous remplissez le formulaire',
            desc: 'Choisissez votre métier et décrivez votre motivation.'
          }, {
            step: '2',
            title: "L'équipe ADCC étudie votre profil",
            desc: 'Nous analysons votre candidature avec attention.'
          }, {
            step: '3',
            title: 'Vous êtes contacté pour une mission',
            desc: 'Nous vous proposons une mission adaptée à vos compétences.'
          }].map((item, idx) => <div key={idx} className="flex-1 flex flex-col items-center text-center relative w-full" data-id="element-863">
                <div className="w-16 h-16 rounded-full bg-[#1E88E5] flex items-center justify-center text-2xl font-bold mb-6 relative z-10 shadow-lg border-4 border-[#2C3E50]" data-id="element-864">
                  {item.step}
                </div>
                {/* Connector line for desktop */}
                {idx < 2 && <div className="hidden md:block absolute top-8 left-[50%] w-full h-0.5 bg-gray-600 -z-0" data-id="element-865"></div>}
                <h3 className="text-xl font-bold mb-3" data-id="element-866">{item.title}</h3>
                <p className="text-gray-400 text-sm px-4" data-id="element-867">{item.desc}</p>
              </div>)}
          </div>
        </div>
      </section>

      {/* 7 & 8. FORMULAIRE & CHARTE */}
      <section id="volunteer-form" className="py-20 bg-gray-50" data-id="element-868">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-869">
          <div className="text-center mb-12" data-id="element-870">
            <h2 className="text-3xl font-bold text-gray-900 mb-4" data-id="element-871">
              Formulaire d'inscription
            </h2>
            <p className="text-lg text-gray-600" data-id="element-872">
              Prêt(e) à nous rejoindre ? Remplissez vos informations ci-dessous.
            </p>
          </div>

          <Card className="shadow-xl border-0" data-id="element-873">
            <CardContent className="p-8 md:p-10" data-id="element-874">
              <form onSubmit={handleSubmit} className="space-y-6" data-id="element-875">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-876">
                  <div data-id="element-877">
                    <label htmlFor="nom" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-878">
                      Nom complet *
                    </label>
                    <input type="text" id="nom" name="nom" required className="w-full rounded-md border-gray-300 shadow-sm focus:border-[#1E88E5] focus:ring-[#1E88E5] p-3 border" placeholder="Votre nom et prénom" data-id="element-879" />
                  </div>
                  <div data-id="element-880">
                    <label htmlFor="telephone" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-881">
                      Téléphone *
                    </label>
                    <input type="tel" id="telephone" name="telephone" required className="w-full rounded-md border-gray-300 shadow-sm focus:border-[#1E88E5] focus:ring-[#1E88E5] p-3 border" placeholder="+229..." data-id="element-882" />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-883">
                  <div data-id="element-884">
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-885">
                      Email *
                    </label>
                    <input type="email" id="email" name="email" required className="w-full rounded-md border-gray-300 shadow-sm focus:border-[#1E88E5] focus:ring-[#1E88E5] p-3 border" placeholder="votre@email.com" data-id="element-886" />
                  </div>
                  <div data-id="element-887">
                    <label htmlFor="quartier" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-888">
                      Ville / Commune *
                    </label>
                    <input type="text" id="quartier" name="quartier" required className="w-full rounded-md border-gray-300 shadow-sm focus:border-[#1E88E5] focus:ring-[#1E88E5] p-3 border" placeholder="Ex: Cotonou" data-id="element-889" />
                  </div>
                </div>

                <div data-id="element-890">
                  <label htmlFor="metier" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-891">
                    Métier souhaité *
                  </label>
                  <select id="metier" name="metier" required value={selectedRole} onChange={e => setSelectedRole(e.target.value)} className="w-full rounded-md border-gray-300 shadow-sm focus:border-[#1E88E5] focus:ring-[#1E88E5] p-3 border bg-white" data-id="element-892">
                    <option value="" data-id="element-893">Sélectionnez un rôle</option>
                    {POLES.flatMap(pole => pole.roles).map(role => <option key={role} value={role} data-id="element-894">
                        {role}
                      </option>)}
                  </select>
                </div>

                <div data-id="element-895">
                  <label htmlFor="disponibilite" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-896">
                    Disponibilités *
                  </label>
                  <select id="disponibilite" name="disponibilite" required className="w-full rounded-md border-gray-300 shadow-sm focus:border-[#1E88E5] focus:ring-[#1E88E5] p-3 border bg-white" data-id="element-897">
                    <option value="" data-id="element-898">Vos disponibilités ?</option>
                    <option value="Ponctuelle" data-id="element-899">Ponctuelle</option>
                    <option value="Week-ends" data-id="element-900">Week-ends</option>
                    <option value="En semaine" data-id="element-901">En semaine</option>
                    <option value="Régulière" data-id="element-902">Régulière</option>
                  </select>
                </div>

                <div data-id="element-903">
                  <label htmlFor="motivation" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-904">
                    Motivation *
                  </label>
                  <textarea id="motivation" name="motivation" required rows={4} className="w-full rounded-md border-gray-300 shadow-sm focus:border-[#1E88E5] focus:ring-[#1E88E5] p-3 border" placeholder="Décrivez brièvement pourquoi vous souhaitez rejoindre l'ADCC et ce rôle en particulier..." data-id="element-905"></textarea>
                </div>

                {/* 8. CHARTE DU BÉNÉVOLE */}
                <div className="pt-6 border-t border-gray-200" data-id="element-906">
                  <h3 className="text-lg font-bold text-gray-900 mb-3" data-id="element-907">
                    Charte du bénévole ADCC
                  </h3>
                  <div className="bg-gray-50 border border-gray-200 rounded-md p-4 h-64 overflow-y-auto text-sm text-gray-700 mb-4 space-y-4" data-id="element-908">
                    <div className="text-center mb-4" data-id="element-909">
                      <p className="font-bold text-gray-900 uppercase text-base" data-id="element-910">
                        Association Dignité Compétence Création (ADCC)
                      </p>
                      <p className="text-gray-500 italic text-xs" data-id="element-911">
                        Cotonou, République du Bénin
                      </p>
                      <p className="font-bold text-gray-900 mt-2" data-id="element-912">
                        CHARTE ÉTHIQUE ET D'ENGAGEMENT
                      </p>
                      <p className="text-gray-600 text-xs" data-id="element-913">
                        À l'usage des membres, bénévoles et collaborateurs
                      </p>
                      <p className="text-gray-400 text-xs italic mt-1" data-id="element-914">
                        Version 1.0 — Adoptée par l'Assemblée Générale
                        constitutive du 10 janvier 2026
                      </p>
                    </div>

                    <div data-id="element-915">
                      <p className="font-bold text-gray-900" data-id="element-916">1. Préambule</p>
                      <p data-id="element-917">
                        L'Association Dignité · Compétence · Création (ADCC)
                        œuvre pour la promotion de la dignité, le développement
                        des compétences et l'autonomisation économique des
                        femmes et des jeunes, à travers des actions de
                        renforcement des capacités et d'accompagnement vers
                        l'emploi.
                      </p>
                      <p className="mt-2" data-id="element-918">
                        La présente charte définit les principes éthiques et les
                        engagements attendus de l'ensemble des membres,
                        bénévoles et collaborateurs de l'ADCC. Elle est
                        complémentaire aux statuts et au règlement d'ordre
                        intérieur.
                      </p>
                      <p className="mt-2" data-id="element-919">
                        Toute personne impliquée dans les activités de l'ADCC à
                        quelque titre que ce soit s'engage à respecter les
                        dispositions de la présente charte dès le début de sa
                        collaboration.
                      </p>
                    </div>

                    <div data-id="element-920">
                      <p className="font-bold text-gray-900" data-id="element-921">
                        2. Valeurs fondamentales
                      </p>
                      <p data-id="element-922">
                        Les actions de l'ADCC reposent sur les valeurs suivantes
                        :
                      </p>
                      <ul className="list-disc pl-5 mt-1 space-y-1" data-id="element-923">
                        <li data-id="element-924">
                          <strong data-id="element-925">Dignité :</strong> respect de la personne
                          humaine en toute circonstance, sans exception ;
                        </li>
                        <li data-id="element-926">
                          <strong data-id="element-927">Intégrité :</strong> honnêteté, transparence
                          et responsabilité dans tous les actes ;
                        </li>
                        <li data-id="element-928">
                          <strong data-id="element-929">Professionnalisme :</strong> rigueur, qualité
                          du travail et engagement constant ;
                        </li>
                        <li data-id="element-930">
                          <strong data-id="element-931">Équité :</strong> traitement juste, non
                          discriminatoire et respectueux de la diversité ;
                        </li>
                        <li data-id="element-932">
                          <strong data-id="element-933">Autonomisation :</strong> promotion de
                          l'indépendance économique et sociale des bénéficiaires
                          ;
                        </li>
                        <li data-id="element-934">
                          <strong data-id="element-935">Responsabilité :</strong> rendre compte de ses
                          actes et assumer les conséquences de ses décisions.
                        </li>
                      </ul>
                    </div>

                    <div data-id="element-936">
                      <p className="font-bold text-gray-900" data-id="element-937">
                        3. Engagements envers les bénéficiaires
                      </p>
                      <p data-id="element-938">Les membres de l'ADCC s'engagent à :</p>
                      <ul className="list-disc pl-5 mt-1 space-y-1" data-id="element-939">
                        <li data-id="element-940">
                          respecter la dignité, l'intégrité et les droits
                          fondamentaux de chaque bénéficiaire ;
                        </li>
                        <li data-id="element-941">
                          garantir un environnement sécurisé, bienveillant et
                          exempt de toute forme d'abus, de discrimination ou de
                          harcèlement ;
                        </li>
                        <li data-id="element-942">
                          fournir des informations claires, honnêtes et non
                          trompeuses sur les activités proposées par l'ADCC ;
                        </li>
                        <li data-id="element-943">
                          ne jamais présenter les ateliers de l'ADCC comme des
                          formations professionnelles certifiantes, ni laisser
                          croire qu'ils donnent lieu à un titre ou diplôme
                          reconnu par l'État ;
                        </li>
                        <li data-id="element-944">
                          ne jamais instrumentaliser les bénéficiaires à des
                          fins de communication ou de collecte de fonds sans
                          leur consentement écrit préalable et éclairé, y
                          compris pour l'utilisation de photographies,
                          témoignages ou données personnelles ;
                        </li>
                        <li data-id="element-945">
                          ne jamais orienter un bénéficiaire vers un employeur
                          sans s'être préalablement assuré du respect de ses
                          droits, de sa sécurité et de sa dignité ;
                        </li>
                        <li data-id="element-946">
                          garantir à tout bénéficiaire un mécanisme de
                          signalement accessible et confidentiel en cas de
                          manquement constaté.
                        </li>
                      </ul>
                    </div>

                    <div data-id="element-947">
                      <p className="font-bold text-gray-900" data-id="element-948">
                        4. Intégrité et comportement professionnel
                      </p>
                      <p data-id="element-949">Les membres s'engagent à :</p>
                      <ul className="list-disc pl-5 mt-1 space-y-1" data-id="element-950">
                        <li data-id="element-951">
                          agir avec honnêteté, loyauté et bonne foi dans toutes
                          leurs relations au sein de l'ADCC et en son nom ;
                        </li>
                        <li data-id="element-952">
                          identifier, déclarer et prévenir tout conflit
                          d'intérêt susceptible d'affecter l'impartialité de
                          leurs actions ;
                        </li>
                        <li data-id="element-953">
                          ne pas utiliser leur position au sein de l'ADCC à des
                          fins personnelles, commerciales ou partisanes ;
                        </li>
                        <li data-id="element-954">
                          respecter les règles et décisions définies par les
                          organes statutaires de l'Association ;
                        </li>
                        <li data-id="element-955">
                          préserver l'image, la réputation et la crédibilité de
                          l'ADCC en toute circonstance.
                        </li>
                      </ul>
                    </div>

                    <div data-id="element-956">
                      <p className="font-bold text-gray-900" data-id="element-957">
                        5. Confidentialité et protection des données
                      </p>
                      <p data-id="element-958">Les membres s'engagent à :</p>
                      <ul className="list-disc pl-5 mt-1 space-y-1" data-id="element-959">
                        <li data-id="element-960">
                          respecter la confidentialité de toutes les
                          informations relatives aux bénéficiaires, partenaires
                          et à la gestion interne de l'Association ;
                        </li>
                        <li data-id="element-961">
                          ne pas divulguer d'informations sensibles à des tiers
                          sans autorisation expresse du Bureau exécutif ;
                        </li>
                        <li data-id="element-962">
                          utiliser les données personnelles collectées
                          uniquement dans le cadre strict des activités de
                          l'ADCC ;
                        </li>
                        <li data-id="element-963">
                          obtenir le consentement éclairé et documenté des
                          bénéficiaires avant toute collecte, publication ou
                          diffusion de données les concernant.
                        </li>
                      </ul>
                    </div>

                    <div data-id="element-964">
                      <p className="font-bold text-gray-900" data-id="element-965">
                        6. Protection des personnes et environnement sûr
                      </p>
                      <p data-id="element-966">
                        L'ADCC s'engage à créer et maintenir un environnement
                        sûr pour toutes les personnes impliquées dans ses
                        activités, en particulier les femmes et les jeunes en
                        situation de vulnérabilité.
                      </p>
                      <p className="mt-2" data-id="element-967">
                        Sont strictement prohibés et passibles de sanctions
                        immédiates :
                      </p>
                      <ul className="list-disc pl-5 mt-1 space-y-1" data-id="element-968">
                        <li data-id="element-969">
                          toute forme de violence physique, psychologique ou
                          verbale ;
                        </li>
                        <li data-id="element-970">tout harcèlement moral ou sexuel ;</li>
                        <li data-id="element-971">
                          toute exploitation, manipulation ou traitement
                          dégradant.
                        </li>
                      </ul>
                      <p className="mt-2" data-id="element-972">
                        Tout membre témoin ou victime d'un tel comportement est
                        tenu de le signaler sans délai au Bureau exécutif.
                      </p>
                    </div>

                    <div data-id="element-973">
                      <p className="font-bold text-gray-900" data-id="element-974">
                        7. Communication et représentation
                      </p>
                      <p data-id="element-975">Les membres s'engagent à :</p>
                      <ul className="list-disc pl-5 mt-1 space-y-1" data-id="element-976">
                        <li data-id="element-977">
                          adopter une communication responsable, respectueuse et
                          conforme aux valeurs de l'ADCC ;
                        </li>
                        <li data-id="element-978">
                          ne diffuser aucune information erronée ou susceptible
                          de nuire à l'image de l'Association ;
                        </li>
                        <li data-id="element-979">
                          qualifier les activités de l'ADCC d'ateliers de
                          renforcement des capacités et non de formations
                          professionnelles certifiantes ;
                        </li>
                        <li data-id="element-980">
                          ne pas engager l'Association ni faire de déclaration
                          publique en son nom sans autorisation expresse du
                          Bureau exécutif.
                        </li>
                      </ul>
                    </div>

                    <div data-id="element-981">
                      <p className="font-bold text-gray-900" data-id="element-982">
                        8. Respect du cadre légal et réglementaire
                      </p>
                      <p data-id="element-983">
                        Les membres s'engagent à respecter la législation et la
                        réglementation en vigueur en République du Bénin, à ne
                        jamais présenter les activités de l'ADCC comme des
                        formations professionnelles certifiantes au sens de la
                        législation béninoise, et à se conformer aux
                        orientations juridiques définies par les organes de
                        l'Association.
                      </p>
                    </div>

                    <div data-id="element-984">
                      <p className="font-bold text-gray-900" data-id="element-985">
                        9. Gestion éthique des ressources
                      </p>
                      <p data-id="element-986">
                        Les membres impliqués dans la gestion financière ou
                        matérielle de l'ADCC s'engagent à utiliser les
                        ressources exclusivement dans l'intérêt de la mission de
                        l'Association, à assurer une traçabilité rigoureuse des
                        fonds, et à signaler sans délai au Bureau exécutif tout
                        manquement ou irrégularité constaté.
                      </p>
                    </div>

                    <div data-id="element-987">
                      <p className="font-bold text-gray-900" data-id="element-988">
                        10. Devoir de signalement envers les partenaires
                      </p>
                      <p data-id="element-989">
                        Tout membre de l'ADCC qui constaterait un comportement
                        d'un partenaire externe — employeur ou autre — contraire
                        aux principes éthiques de l'Association est tenu d'en
                        informer sans délai le Bureau exécutif, afin que
                        celui-ci puisse prendre les mesures appropriées
                        conformément à la charte des employeurs partenaires.
                      </p>
                    </div>

                    <div data-id="element-990">
                      <p className="font-bold text-gray-900" data-id="element-991">
                        11. Engagement personnel
                      </p>
                      <p data-id="element-992">
                        En intégrant l'ADCC, chaque membre s'engage à contribuer
                        activement et de bonne foi aux missions de
                        l'Association, à respecter les décisions des organes
                        statutaires, à faire preuve de professionnalisme et de
                        respect envers toutes les parties prenantes, et à agir
                        en toute circonstance dans l'intérêt de l'Association et
                        de ses bénéficiaires.
                      </p>
                    </div>

                    <div data-id="element-993">
                      <p className="font-bold text-gray-900" data-id="element-994">
                        12. Sanctions en cas de non-respect
                      </p>
                      <p data-id="element-995">
                        Le non-respect de la présente charte peut entraîner,
                        selon la gravité des faits : un avertissement écrit, une
                        suspension temporaire, ou une exclusion de
                        l'Association. La procédure disciplinaire est celle
                        prévue par le règlement d'ordre intérieur. Le membre
                        concerné est entendu avant toute décision. En cas de
                        faute grave, le Bureau exécutif peut prononcer une
                        suspension conservatoire immédiate.
                      </p>
                    </div>

                    <div data-id="element-996">
                      <p className="font-bold text-gray-900" data-id="element-997">13. Acceptation</p>
                      <p data-id="element-998">
                        En cochant la case ci-dessous, je déclare avoir pris
                        connaissance de l'intégralité de la Charte éthique et
                        d'engagement interne de l'ADCC et m'engage à en
                        respecter scrupuleusement les principes.
                      </p>
                    </div>

                    <div className="text-center text-xs text-gray-400 mt-4 pt-4 border-t border-gray-200" data-id="element-999">
                      <p data-id="element-1000">
                        Charte éthique interne ADCC — Version 1.0 — Janvier 2026
                      </p>
                      <p data-id="element-1001">
                        Association Dignité · Compétence · Création (ADCC) —
                        Cotonou, Bénin — www.adcc.africa
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3" data-id="element-1002">
                    <div className="flex items-center h-5" data-id="element-1003">
                      <input id="charter" name="charter" type="checkbox" checked={charterAccepted} onChange={e => setCharterAccepted(e.target.checked)} className="w-5 h-5 text-[#1E88E5] border-gray-300 rounded focus:ring-[#1E88E5] cursor-pointer" data-id="element-1004" />
                    </div>
                    <label htmlFor="charter" className="text-sm text-gray-700 cursor-pointer" data-id="element-1005">
                      J'ai lu et j'accepte la charte du bénévole ADCC *
                    </label>
                  </div>
                </div>

                <div className="pt-4" data-id="element-1006">
                  <Button type="submit" className="w-full text-lg py-6" size="lg" disabled={isLoading || !charterAccepted} data-id="element-1007">
                    {isLoading ? 'Envoi en cours...' : 'Envoyer ma candidature'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>
    </Layout>;
}